import { Camera } from '../render/camera.ts';
import { CommandDispatcher } from '../app/commands.ts';
import { GameState } from '../domain/types.ts';
import { GAME_CONFIG } from '../config/gameConfig.ts';

export class PointerController {
  private canvas: HTMLCanvasElement;
  private camera: Camera;
  private dispatcher: CommandDispatcher;
  private getState: () => Readonly<GameState>;

  private isDragging = false;
  private lastPointerX = 0;
  private lastPointerY = 0;
  private pointerDownTime = 0;
  private activePointers = new Map<number, { x: number; y: number }>();
  private initialPinchDist = 0;

  constructor(
    canvas: HTMLCanvasElement,
    camera: Camera,
    dispatcher: CommandDispatcher,
    getState: () => Readonly<GameState>
  ) {
    this.canvas = canvas;
    this.camera = camera;
    this.dispatcher = dispatcher;
    this.getState = getState;

    this.setupListeners();
  }

  private setupListeners(): void {
    this.canvas.addEventListener('pointerdown', this.handlePointerDown);
    this.canvas.addEventListener('wheel', this.handleWheel, { passive: false });
    window.addEventListener('pointermove', this.handlePointerMove);
    window.addEventListener('pointerup', this.handlePointerUp);
    window.addEventListener('pointercancel', this.handlePointerUp);
  }

  public destroy(): void {
    this.canvas.removeEventListener('pointerdown', this.handlePointerDown);
    this.canvas.removeEventListener('wheel', this.handleWheel);
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('pointercancel', this.handlePointerUp);
  }

  private handleWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 0.87;
    const rect = this.canvas.getBoundingClientRect();
    this.camera.zoomBy(factor, e.clientX - rect.left, e.clientY - rect.top);
  };

  private handlePointerDown = (e: PointerEvent): void => {
    this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.activePointers.size === 1) {
      this.isDragging = true;
      this.lastPointerX = e.clientX;
      this.lastPointerY = e.clientY;
      this.pointerDownTime = Date.now();
    } else if (this.activePointers.size === 2) {
      this.isDragging = false; // Pinching
      const pts = Array.from(this.activePointers.values());
      this.initialPinchDist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
    }
  };

  private handlePointerMove = (e: PointerEvent): void => {
    if (this.activePointers.has(e.pointerId)) {
      this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    if (this.activePointers.size === 2) {
      // Pinch-to-zoom
      const pts = Array.from(this.activePointers.values());
      const currentDist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
      if (this.initialPinchDist > 5 && currentDist > 5) {
        const factor = currentDist / this.initialPinchDist;
        const rect = this.canvas.getBoundingClientRect();
        const midX = (pts[0]!.x + pts[1]!.x) / 2 - rect.left;
        const midY = (pts[0]!.y + pts[1]!.y) / 2 - rect.top;
        this.camera.zoomBy(factor, midX, midY);
        this.initialPinchDist = currentDist;
      }
      return;
    }

    if (!this.isDragging) return;

    const deltaX = e.clientX - this.lastPointerX;
    const deltaY = e.clientY - this.lastPointerY;

    this.lastPointerX = e.clientX;
    this.lastPointerY = e.clientY;

    this.camera.pan(deltaX, deltaY);
  };

  private handlePointerUp = (e: PointerEvent): void => {
    this.activePointers.delete(e.pointerId);

    if (this.activePointers.size === 0) {
      const wasDragging = this.isDragging;
      this.isDragging = false;
      const duration = Date.now() - this.pointerDownTime;
      // Nếu tap nhanh (<= 250ms), kiểm tra va chạm với node
      if (wasDragging && duration <= 250) {
        this.handleTap(e.clientX, e.clientY);
      }
    } else if (this.activePointers.size === 1) {
      // Còn 1 ngón sau pinch
      const remaining = Array.from(this.activePointers.values())[0]!;
      this.isDragging = true;
      this.lastPointerX = remaining.x;
      this.lastPointerY = remaining.y;
    }
  };

  private handleTap(clientX: number, clientY: number): void {
    const rect = this.canvas.getBoundingClientRect();
    const screenX = clientX - rect.left;
    const screenY = clientY - rect.top;

    const state = this.getState();
    if (!state.neighborhood) return;

    const hitRadius = GAME_CONFIG.render.minTouchTarget / 2;
    // 1. Kiểm tra va chạm với Ghost Rivals (đối thủ)
    for (const ghost of state.ghostStalls) {
      const ghostScreen = this.camera.worldToScreen({ x: ghost.x, y: ghost.y });
      const dist = Math.hypot(screenX - ghostScreen.x, screenY - ghostScreen.y);

      if (dist <= hitRadius + 4) {
        if (navigator.vibrate) navigator.vibrate(15);
        this.dispatcher.dispatch({
          type: 'INSPECT_RIVAL',
          rivalId: ghost.sid,
        });
        return;
      }
    }

    // 2. Kiểm tra va chạm với Business Nodes
    let clickedNodeId: string | null = null;
    for (const node of state.neighborhood.nodes) {
      const nodeScreen = this.camera.worldToScreen({ x: node.x, y: node.y });
      const dist = Math.hypot(screenX - nodeScreen.x, screenY - nodeScreen.y);

      if (dist <= hitRadius) {
        clickedNodeId = node.id;
        break;
      }
    }

    if (navigator.vibrate) {
      navigator.vibrate(10); // Haptic feedback
    }

    this.dispatcher.dispatch({
      type: 'INSPECT_NODE',
      nodeId: clickedNodeId,
    });
  }
}
